import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, ChevronLeft, ChevronRight, Calendar, Pencil, Save, X, Download, Building2, CreditCard, Landmark, Link2, Unlink, Briefcase, User, Tag, TrendingUp, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_CATEGORIES = ["Travel", "Food & Dining", "Shopping", "Transportation", "Services", "Subscriptions", "Utilities", "Entertainment", "Health", "Education", "Fees & Charges", "Home", "Personal Care", "Insurance", "Fuel", "Other"];
const INCOME_CATEGORIES = ["Salary", "Freelance", "Business Income", "Investment Returns", "Interest", "Rental Income", "Refund", "Cashback", "Gift", "Other Income"];
const fmtINR = (v) => v?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
const fmtMonth = (m) => { if (!m || m.length < 7) return m; const [y, mo] = m.split('-'); const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${MONTHS[parseInt(mo) - 1]} ${y.slice(2)}`; };

const INVESTMENT_CATEGORIES = new Set(['mf purchase', 'fd investment', 'mf utilities', 'mf redemption', 'fd closure']);
const TRANSFER_CATEGORIES = new Set(['cc payment', 'bank transfer', 'internal transfer', 'personal transfer']);

const amtColor = (tx) => {
  const catLower = (tx.category || '').toLowerCase();
  if (INVESTMENT_CATEGORIES.has(catLower)) return 'text-[#7C6B4F]';
  if (TRANSFER_CATEGORIES.has(catLower)) return 'text-blue-600';
  if (tx.is_credit) return 'text-emerald-700';
  const t = tx.tag || '';
  if (t.startsWith('Business') || t === 'business') return 'text-red-600';
  if (t.startsWith('Personal') || t === 'personal') return 'text-orange-600';
  return 'text-[#1C1C19]';
};

function TagBadge({ tag, isCredit, category }) {
  const baseTag = tag?.startsWith('Business') || tag === 'business' ? 'business'
    : tag?.startsWith('Personal') || tag === 'personal' ? 'personal'
    : tag?.startsWith('Income') || tag === 'income' ? 'income'
    : tag === 'junk' ? 'junk' : 'untagged';

  const catLower = (category || '').toLowerCase();
  const isCCPayment = catLower.includes('cc payment');
  const isInvestmentOut = ['mf purchase', 'fd investment', 'mf utilities'].includes(catLower);
  const isInvestmentIn = ['mf redemption', 'fd closure'].includes(catLower);
  const isTransfer = ['bank transfer', 'internal transfer', 'personal transfer'].includes(catLower);

  let displayTag;
  if (isCCPayment) displayTag = 'cc_payment';
  else if (isInvestmentOut || isInvestmentIn) displayTag = 'investment';
  else if (isTransfer) displayTag = 'transfer';
  else if (isCredit && baseTag !== 'junk' && baseTag !== 'untagged') displayTag = 'income';
  else displayTag = baseTag;

  const styles = {
    business: 'text-red-600',
    personal: 'text-orange-600',
    cc_payment: 'text-blue-600',
    income: 'text-emerald-700',
    investment: 'text-[#7C6B4F]',
    transfer: 'text-blue-600',
    junk: 'text-[#5C5C55]',
    untagged: 'text-[#5C5C55]',
  };
  const labels = {
    business: 'Expense - Business',
    personal: 'Expense - Personal',
    cc_payment: 'Payment Made',
    income: 'Income',
    investment: 'Investment',
    transfer: 'Transfer',
    junk: 'Junk',
    untagged: 'Untagged',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wide ${styles[displayTag] || styles.untagged}`}>
      {displayTag === 'business' && <Briefcase size={10} />}
      {displayTag === 'personal' && <User size={10} />}
      {displayTag === 'income' && <TrendingUp size={10} />}
      {displayTag === 'cc_payment' && <CreditCard size={10} />}
      {displayTag === 'investment' && <TrendingUp size={10} />}
      {displayTag === 'transfer' && <Link2 size={10} />}
      {displayTag === 'untagged' && <Tag size={10} />}
      {labels[displayTag] || tag}
    </span>
  );
}

function EntityIcon({ source }) {
  const s = (source || '').toLowerCase();
  if (s.includes('icici')) return <CreditCard size={12} className="text-[#D36135]" />;
  if (s.includes('hdfc')) return <CreditCard size={12} className="text-blue-600" />;
  if (s.includes('axis')) return <CreditCard size={12} className="text-[#832561]" />;
  if (s.includes('sbi')) return <Landmark size={12} className="text-blue-800" />;
  return <Building2 size={12} className="text-[#5C5C55]" />;
}

function SortHeader({ label, field, currentSort, onSort }) {
  const isActive = currentSort.field === field;
  const isAsc = isActive && currentSort.order === 'asc';
  return (
    <button onClick={() => onSort(field)} data-testid={`biz-sort-${field}`}
      className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-medium hover:text-[#1C1C19] transition-colors">
      {label}
      {isActive ? (isAsc ? <ArrowUp size={10} className="text-[#284A33]" /> : <ArrowDown size={10} className="text-[#284A33]" />) : <ArrowUpDown size={10} className="opacity-30" />}
    </button>
  );
}

export default function BusinessTransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [flowTab, setFlowTab] = useState('all');
  const [filters, setFilters] = useState({ search: '', source: 'all', month: 'all', category: 'all', fy: 'all' });
  const [sort, setSort] = useState({ field: 'date', order: 'desc' });
  const [filterOptions, setFilterOptions] = useState({ sources: [], months: [], categories: [], fys: [] });
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({ tag: '', sub_category: '', category: '' });
  const [selected, setSelected] = useState(new Set());
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [subCatOptions, setSubCatOptions] = useState({});
  const [catDialog, setCatDialog] = useState({ open: false, customName: '' });
  const [othersDialog, setOthersDialog] = useState({ open: false, parentTag: '', customName: '' });
  const [linkDialog, setLinkDialog] = useState({ open: false, note: '' });
  const [linksMap, setLinksMap] = useState({});
  const [expandedLinks, setExpandedLinks] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const limit = 50;

  const switchTab = (tab) => {
    setActiveTab(tab);
    setFlowTab('all');
    setFilters({ search: '', source: 'all', month: 'all', category: 'all', fy: 'all' });
    setPage(1);
    setSelected(new Set());
  };

  const switchFlowTab = (flow) => {
    setFlowTab(flow);
    setPage(1);
    setSelected(new Set());
  };

  const fetchFilters = useCallback(async () => {
    try {
      const [fRes, scRes, catRes] = await Promise.all([
        axios.get(`${API}/business/transactions/filters`, { withCredentials: true }),
        axios.get(`${API}/sub-categories`, { withCredentials: true }),
        axios.get(`${API}/categories`, { withCredentials: true }),
      ]);
      setFilterOptions(fRes.data);
      if (fRes.data.categories) setCategories(fRes.data.categories.sort());
      setSubCatOptions(scRes.data);
    } catch (err) { console.error('Filters error:', err); }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort_by: sort.field, sort_order: sort.order };
      if (filters.search) params.search = filters.search;
      if (filters.source !== 'all') params.source = filters.source;
      if (filters.month !== 'all') params.month = filters.month;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.fy !== 'all') params.fy = filters.fy;
      if (flowTab === 'inflow') params.flow = 'inflow';
      else if (flowTab === 'outflow') params.flow = 'outflow';
      // Tab-based tag filter
      if (activeTab === 'pending') params.tag = 'untagged';
      // Junk filter
      if (flowTab === 'junk') params.junk = 'only';

      const { data } = await axios.get(`${API}/business/transactions`, { params, withCredentials: true });
      setTransactions(data.transactions);
      setTotal(data.total);
      setPages(Math.ceil(data.total / limit));
      setLinksMap(data.links || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, sort, filters, activeTab, flowTab]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const handleSort = (field) => { setSort(prev => ({ field, order: prev.field === field ? (prev.order === 'desc' ? 'asc' : 'desc') : 'asc' })); setPage(1); };
  const handleFilterChange = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); setSelected(new Set()); };
  const toggleSelect = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleAll = () => { setSelected(selected.size === transactions.length ? new Set() : new Set(transactions.map(t => t.id))); };

  // Inline edit
  const startEdit = (tx) => {
    setEditingRow(tx.id);
    setEditData({ tag: tx.tag || 'untagged', sub_category: tx.sub_category || '', category: tx.category || 'Other' });
  };
  const cancelEdit = () => { setEditingRow(null); setEditData({ tag: '', sub_category: '', category: '' }); };
  const saveEdit = async (txId) => {
    try {
      const promises = [];
      const tx = transactions.find(t => t.id === txId);
      if (editData.tag !== tx.tag) {
        promises.push(axios.patch(`${API}/transactions/${txId}/tag`, { tag: editData.tag }, { withCredentials: true }));
      }
      if (editData.category !== tx.category) {
        promises.push(axios.patch(`${API}/transactions/${txId}/category`, { category: editData.category }, { withCredentials: true }));
      }
      if (editData.sub_category !== (tx.sub_category || '') && editData.sub_category && editData.sub_category !== '__none__') {
        promises.push(axios.patch(`${API}/transactions/${txId}/sub-category`, { sub_category: editData.sub_category }, { withCredentials: true }));
      }
      if (editData.sub_category === '__none__' && tx.sub_category) {
        promises.push(axios.patch(`${API}/transactions/${txId}/sub-category`, { sub_category: '' }, { withCredentials: true }));
      }
      await Promise.all(promises);
      toast.success('Saved');
      setEditingRow(null);
      fetchTransactions();
    } catch { toast.error('Save failed'); }
  };

  const handleCatChange = (v) => {
    if (v === '__add__') { setCatDialog({ open: true, customName: '' }); return; }
    setEditData(d => ({ ...d, category: v }));
  };
  const handleSubCatChange = (v) => {
    if (v === '__others__') { setOthersDialog({ open: true, parentTag: editData.tag, customName: '' }); return; }
    setEditData(d => ({ ...d, sub_category: v }));
  };
  const saveCustomCategory = async () => {
    const name = catDialog.customName.trim();
    if (!name) { toast.error('Enter a name'); return; }
    try {
      await axios.post(`${API}/categories`, { name }, { withCredentials: true });
      const { data } = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(data.categories || DEFAULT_CATEGORIES);
      setEditData(d => ({ ...d, category: name }));
      setCatDialog({ open: false, customName: '' });
      toast.success(`"${name}" added`);
    } catch { toast.error('Failed'); }
  };
  const saveCustomSubCategory = async () => {
    const name = othersDialog.customName.trim();
    if (!name) { toast.error('Enter a name'); return; }
    try {
      await axios.post(`${API}/sub-categories`, { name, parent_tag: othersDialog.parentTag }, { withCredentials: true });
      const { data } = await axios.get(`${API}/sub-categories`, { withCredentials: true });
      setSubCatOptions(data);
      setEditData(d => ({ ...d, sub_category: name }));
      setOthersDialog({ open: false, parentTag: '', customName: '' });
      toast.success(`"${name}" added`);
    } catch { toast.error('Failed'); }
  };

  // Bulk actions
  const bulkTag = async (tag) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await axios.post(`${API}/transactions/bulk-tag`, { transaction_ids: ids, tag }, { withCredentials: true });
      setSelected(new Set());
      toast.success(`Tagged ${ids.length} as ${tag}`);
      fetchTransactions();
    } catch { toast.error('Bulk tag failed'); }
  };

  const linkSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length < 2) { toast.error('Select at least 2 transactions'); return; }
    try {
      await axios.post(`${API}/transactions/link`, { transaction_ids: ids, note: linkDialog.note }, { withCredentials: true });
      setSelected(new Set());
      setLinkDialog({ open: false, note: '' });
      toast.success('Transactions linked as split entry');
      fetchTransactions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
  };

  const unlinkGroup = async (linkId) => {
    try {
      await axios.delete(`${API}/transactions/link/${linkId}`, { withCredentials: true });
      toast.success('Transactions unlinked');
      setExpandedLinks(prev => { const n = new Set(prev); n.delete(linkId); return n; });
      fetchTransactions();
    } catch { toast.error('Unlink failed'); }
  };

  const toggleLinkExpand = (linkId) => {
    setExpandedLinks(prev => { const n = new Set(prev); n.has(linkId) ? n.delete(linkId) : n.add(linkId); return n; });
  };

  // Group linked transactions
  const groupedTransactions = (() => {
    const result = [];
    const seen = new Set();
    for (const tx of transactions) {
      if (tx.link_id && linksMap[tx.link_id]) {
        if (seen.has(tx.link_id)) continue;
        seen.add(tx.link_id);
        const linkInfo = linksMap[tx.link_id];
        const children = transactions.filter(t => t.link_id === tx.link_id);
        result.push({
          _isGroup: true, link_id: tx.link_id, note: linkInfo.note,
          total_amount: linkInfo.total_amount, earliest_date: linkInfo.earliest_date,
          children, tag: children[0]?.tag || 'business',
          category: children[0]?.category, source: children[0]?.source,
          card_last_four: children[0]?.card_last_four,
          is_credit: (() => { const cs = children.filter(c => c.is_credit).reduce((s, c) => s + c.amount, 0); return Math.abs(cs - linkInfo.total_amount) < 1; })(),
          fy_india: children[0]?.fy_india,
        });
      } else {
        result.push(tx);
      }
    }
    return result;
  })();

  // Download Excel
  const downloadExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.source !== 'all') params.source = filters.source;
      if (filters.fy !== 'all') params.fy = filters.fy;
      params.statement_category = 'business';
      const res = await axios.get(`${API}/transactions/export-excel`, { params, withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'business_transactions.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const sources = filterOptions.sources || [];

  return (
    <div className="p-6 lg:p-8 space-y-4 max-w-[1500px]" data-testid="business-transactions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Transactions</h2>
        <Button onClick={downloadExcel} disabled={exporting || total === 0} data-testid="biz-download-excel"
          className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-8 px-3">
          <Download size={13} className="mr-1.5" />{exporting ? 'Exporting...' : `Download Excel (${total})`}
        </Button>
      </div>

      {/* Pending / All Tabs */}
      <div className="flex items-center border-b border-[#E2E0D8]" data-testid="biz-transaction-tabs">
        <button onClick={() => switchTab('pending')} data-testid="biz-tab-pending"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${activeTab === 'pending' ? 'text-[#284A33]' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          Pending
          {activeTab === 'pending' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#284A33] rounded-t" />}
        </button>
        <button onClick={() => switchTab('all')} data-testid="biz-tab-all"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${activeTab === 'all' ? 'text-[#284A33]' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          All
          {activeTab === 'all' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#284A33] rounded-t" />}
        </button>
        {/* Flow sub-tabs */}
        <div className="ml-auto flex items-center gap-1 pr-1" data-testid="biz-flow-tabs">
          {[
            { key: 'all', label: 'All' },
            { key: 'inflow', label: 'Inflow' },
            { key: 'outflow', label: 'Outflow' },
            { key: 'junk', label: 'Junk' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => switchFlowTab(key)} data-testid={`biz-flow-${key}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${flowTab === key ? (key === 'junk' ? 'bg-[#5C5C55] text-white' : 'bg-[#284A33] text-white') : 'text-[#5C5C55] hover:bg-[#E8E6DE]'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Source pills */}
      {sources.length > 0 && (
        <div className="flex items-center gap-1 overflow-x-auto pb-1 -mt-1" data-testid="biz-source-pills">
          <button onClick={() => handleFilterChange('source', 'all')} data-testid="biz-source-all"
            className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${filters.source === 'all' ? 'bg-[#1C1C19] text-white' : 'text-[#5C5C55] hover:bg-[#E8E6DE] border border-[#E2E0D8]'}`}>
            All
          </button>
          {sources.map(s => (
            <button key={s.label || s.source} onClick={() => handleFilterChange('source', s.source || s.label)}
              data-testid={`biz-source-${(s.source || s.label || '').replace(/\s+/g, '-')}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                filters.source === (s.source || s.label) ? 'bg-[#1C1C19] text-white' : 'text-[#5C5C55] hover:bg-[#E8E6DE] border border-[#E2E0D8]'
              }`}>
              {s.label || s.source} <span className="text-[10px] opacity-60 ml-0.5">{s.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="biz-transaction-filters">
        <div className="relative flex-1 min-w-[160px] max-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C55]" />
          <Input data-testid="biz-search" placeholder="Search transactions..." value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)} className="pl-8 border-[#E2E0D8] h-8 text-xs" />
        </div>
        <Select value={filters.month} onValueChange={v => handleFilterChange('month', v)}>
          <SelectTrigger className="w-[120px] h-8 text-xs border-[#E2E0D8]" data-testid="biz-filter-month">
            <Calendar size={12} className="mr-1 text-[#5C5C55]" /><SelectValue placeholder="All Months" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {(filterOptions.months || []).map(m => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* category filter moved to capsule row below */}
        <Select value={filters.fy} onValueChange={v => handleFilterChange('fy', v)}>
          <SelectTrigger className="w-[110px] h-8 text-xs border-[#E2E0D8]" data-testid="biz-filter-fy"><SelectValue placeholder="All FYs" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All FYs</SelectItem>
            {(filterOptions.fys || []).map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-[#5C5C55] ml-auto font-mono">{total} results</span>
      </div>

      {/* Category Capsules */}
      {activeTab !== 'pending' && (filterOptions.categories || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="biz-category-capsules">
          <button
            data-testid="biz-category-capsule-all"
            onClick={() => handleFilterChange('category', 'all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
              filters.category === 'all'
                ? 'bg-[#284A33] text-white border-[#284A33]'
                : 'bg-white text-[#5C5C55] border-[#E2E0D8] hover:border-[#284A33]/40 hover:text-[#284A33]'
            }`}
          >
            All
          </button>
          {filterOptions.categories.map(c => (
            <button
              key={c}
              data-testid={`biz-category-capsule-${c.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              onClick={() => handleFilterChange('category', c)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                filters.category === c
                  ? 'bg-[#284A33] text-white border-[#284A33]'
                  : 'bg-white text-[#5C5C55] border-[#E2E0D8] hover:border-[#284A33]/40 hover:text-[#284A33]'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-[#284A33]/5 border border-[#284A33]/15 rounded-md px-4 py-2.5" data-testid="biz-bulk-actions">
          <span className="text-sm text-[#284A33] font-medium">{selected.size} selected</span>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" onClick={() => bulkTag('personal')} className="bg-[#4A7A5D]/10 text-[#4A7A5D] hover:bg-[#4A7A5D]/20 border border-[#4A7A5D]/20 h-7 text-xs"><User size={12} className="mr-1" /> Personal</Button>
            <Button size="sm" onClick={() => bulkTag('business')} className="bg-[#D36135]/10 text-[#D36135] hover:bg-[#D36135]/20 border border-[#D36135]/20 h-7 text-xs"><Briefcase size={12} className="mr-1" /> Business</Button>
            {selected.size >= 2 && (
              <Button size="sm" onClick={() => setLinkDialog({ open: true, note: '' })} data-testid="biz-link-split-btn"
                className="bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 h-7 text-xs"><Link2 size={12} className="mr-1" /> Link as Split</Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => bulkTag('untagged')} className="text-[#5C5C55] hover:bg-[#E8E6DE] h-7 text-xs">Clear</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#E2E0D8] rounded-md overflow-hidden">
        {loading ? <div className="p-8 text-center text-sm text-[#5C5C55]">Loading...</div> :
         transactions.length === 0 ? (
           <div className="p-12 text-center">
             <Building2 size={32} className="mx-auto mb-2 text-[#D5D2C6]" />
             <span className="text-sm text-[#5C5C55]">{activeTab === 'pending' ? 'No pending transactions — all categorized' : 'No business transactions found'}</span>
           </div>
         ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="biz-transactions-table">
              <thead>
                <tr className="border-b border-[#E2E0D8] bg-[#F7F6F2]/50">
                  <th className="w-8 p-2"><input type="checkbox" checked={selected.size === transactions.length && transactions.length > 0} onChange={toggleAll} className="rounded border-[#D5D2C6]" data-testid="biz-select-all" /></th>
                  <th className="text-left p-2 text-[#5C5C55]"><SortHeader label="Date" field="date" currentSort={sort} onSort={handleSort} /></th>
                  <th className="text-left p-2 text-[#5C5C55]"><SortHeader label="Description" field="merchant" currentSort={sort} onSort={handleSort} /></th>
                  <th className="text-left p-2 text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium">Entity</th>
                  <th className="text-left p-2 text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium">Tag</th>
                  <th className="text-right p-2 text-[#5C5C55]"><SortHeader label="Amount" field="amount" currentSort={sort} onSort={handleSort} /></th>
                  <th className="w-10 p-2 text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium text-center">Edit</th>
                </tr>
              </thead>
              <tbody>
                {groupedTransactions.map((item) => {
                  if (item._isGroup) {
                    const isExpanded = expandedLinks.has(item.link_id);
                    return (
                      <React.Fragment key={`link-${item.link_id}`}>
                        <tr data-testid={`biz-linked-group-${item.link_id}`}
                          className="border-b border-blue-200/60 bg-blue-50/30 hover:bg-blue-50/60 transition-colors cursor-pointer"
                          onClick={() => toggleLinkExpand(item.link_id)}>
                          <td className="p-2"><input type="checkbox" checked={false} readOnly className="rounded border-[#D5D2C6] opacity-30" /></td>
                          <td className="p-2">
                            <div className="text-sm font-mono whitespace-nowrap text-[#1C1C19]">{item.earliest_date}</div>
                            <div className="text-[10px] font-mono text-[#5C5C55]/60">{item.fy_india}</div>
                          </td>
                          <td className="p-2">
                            <div className="text-sm font-medium text-[#1C1C19]">
                              {item.children[0]?.category || item.children[0]?.merchant || '—'}
                            </div>
                            {item.note && <div className="text-[10px] text-blue-600">{item.note}</div>}
                            <div className="mt-0.5 space-y-px">
                              {item.children.map((c, i) => (
                                <div key={i} className="text-[10px] text-[#5C5C55] font-mono truncate max-w-[300px]">{c.sub_category || c.merchant?.split(' ').slice(0, 3).join(' ') || 'Other'}</div>
                              ))}
                            </div>
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-1"><EntityIcon source={item.source} /><span className="text-[11px] text-[#5C5C55] font-mono">{item.source || '?'}</span></div>
                          </td>
                          <td className="p-2"><TagBadge tag={item.tag} isCredit={item.is_credit} category={item.category} /></td>
                          <td className="p-2 text-right">
                            <span className={`font-mono text-sm font-semibold ${amtColor({is_credit: item.is_credit, tag: item.tag})}`}>
                              {item.is_credit ? '+' : ''}₹{fmtINR(item.total_amount)}
                            </span>
                            <div className="mt-0.5 space-y-px">
                              {item.children.map((c, i) => (
                                <div key={i} className={`text-[10px] font-mono ${amtColor(c)}`}>{c.is_credit ? '+' : ''}₹{fmtINR(c.amount)}</div>
                              ))}
                            </div>
                          </td>
                          <td className="p-2">
                            <button onClick={(e) => { e.stopPropagation(); unlinkGroup(item.link_id); }} title="Unlink"
                              data-testid={`biz-unlink-${item.link_id}`}
                              className="p-1 rounded text-[#5C5C55] hover:text-red-600 hover:bg-red-50 transition-all"><Unlink size={12} /></button>
                          </td>
                        </tr>
                        {isExpanded && item.children.map((tx) => (
                          <tr key={tx.id} data-testid={`biz-txn-row-${tx.id}`} className="border-b border-blue-100/60 bg-blue-50/15 transition-colors">
                            <td className="p-2"></td>
                            <td className="p-2"><div className="text-sm font-mono whitespace-nowrap text-[#1C1C19]/70">{tx.date}</div></td>
                            <td className="p-2"><div className="text-sm text-[#1C1C19]/70 truncate max-w-[450px]">{tx.category || tx.merchant || '—'}</div></td>
                            <td className="p-2"><span className="text-[11px] text-[#5C5C55]/60 font-mono">{tx.source}</span></td>
                            <td className="p-2"><TagBadge tag={tx.tag} isCredit={tx.is_credit} category={tx.category} /></td>
                            <td className="p-2 text-right"><span className={`font-mono text-sm ${amtColor(tx)} opacity-70`}>{tx.is_credit ? '+' : ''}₹{fmtINR(tx.amount)}</span></td>
                            <td className="p-2"></td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  }

                  // Regular transaction row
                  const tx = item;
                  const isEditing = editingRow === tx.id;
                  const subCats = isEditing ? (subCatOptions[editData.tag] || []) : [];
                  return (
                    <tr key={tx.id} data-testid={`biz-txn-row-${tx.id}`}
                      className={`border-b border-[#E2E0D8]/60 transition-colors ${isEditing ? 'bg-[#284A33]/[0.02]' : 'hover:bg-[#F7F6F2]/50'}`}>
                      <td className="p-2"><input type="checkbox" checked={selected.has(tx.id)} onChange={() => toggleSelect(tx.id)} className="rounded border-[#D5D2C6]" data-testid={`biz-select-${tx.id}`} /></td>
                      {/* Date */}
                      <td className="p-2">
                        <div className="text-sm font-mono whitespace-nowrap text-[#1C1C19]">{tx.date}</div>
                        <div className="text-[10px] font-mono text-[#5C5C55]/60">{tx.fy_india}</div>
                      </td>
                      {/* Description — show category (matches filter), sub_category as secondary */}
                      <td className="p-2">
                        <div className="flex items-center gap-1.5">
                          <div title={tx.merchant || ''} className="text-sm font-medium truncate max-w-[450px] cursor-help text-[#1C1C19]">
                            {(!tx.tag || tx.tag === 'untagged') ? (tx.merchant || '—') : (tx.category || tx.merchant || '—')}
                          </div>
                          {tx.is_junk && flowTab !== 'junk' && <span className="text-[9px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-mono shrink-0">Junk</span>}
                        </div>
                        {(!tx.tag || tx.tag === 'untagged') ? (
                          tx.location && <div title={tx.location} className="text-[10px] text-[#5C5C55] truncate max-w-[350px] cursor-help">{tx.location}</div>
                        ) : (
                          <>
                            {tx.sub_category && tx.sub_category !== tx.category && (
                              <div className="text-[10px] text-[#5C5C55] truncate max-w-[400px]">{tx.sub_category}</div>
                            )}
                            {tx.location && <div title={tx.location} className="text-[10px] text-[#5C5C55]/60 truncate max-w-[350px] cursor-help">{tx.location}</div>}
                          </>
                        )}
                      </td>
                      {/* Entity */}
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <EntityIcon source={tx.source} />
                          <span className="text-[11px] text-[#5C5C55] font-mono">{tx.source || '?'}</span>
                        </div>
                        {tx.card_last_four && tx.card_last_four !== 'None' && <div className="text-[10px] font-mono text-[#5C5C55]/60">****{tx.card_last_four}</div>}
                      </td>
                      {/* Tag */}
                      <td className="p-2">
                        {isEditing ? (
                          <div className="flex flex-col gap-1.5">
                            <Select value={editData.tag} onValueChange={(v) => setEditData(d => ({ ...d, tag: v, sub_category: '' }))}>
                              <SelectTrigger className="h-7 w-[120px] text-xs border-[#284A33]/30"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="untagged">Untagged</SelectItem>
                                {tx.is_credit ? (
                                  <SelectItem value="income">Income</SelectItem>
                                ) : (
                                  <><SelectItem value="personal">Personal</SelectItem><SelectItem value="business">Business</SelectItem></>
                                )}
                              </SelectContent>
                            </Select>
                            {(editData.tag === 'personal' || editData.tag === 'business') && (
                              <Select value={editData.sub_category || '__none__'} onValueChange={handleSubCatChange}>
                                <SelectTrigger className="h-6 w-[130px] text-[10px] border-[#E2E0D8]"><SelectValue placeholder="Sub-category" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__" className="text-[#999]">No sub-category</SelectItem>
                                  {subCats.map(sc => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}
                                  <SelectItem value="__others__" className="text-[#284A33] font-medium">+ Others (custom)</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            <Select value={editData.category} onValueChange={handleCatChange}>
                              <SelectTrigger className="h-7 w-[120px] text-xs border-[#E2E0D8]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(tx.is_credit ? INCOME_CATEGORIES : categories).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                <SelectItem value="__add__" className="text-[#284A33] font-medium">+ Add Category</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <TagBadge tag={tx.tag} isCredit={tx.is_credit} category={tx.category} />
                        )}
                      </td>
                      {/* Amount */}
                      <td className="p-2 text-right">
                        <span className={`font-mono text-sm ${amtColor(tx)}`}>
                          {tx.is_credit ? '+' : ''}₹{fmtINR(tx.amount)}
                        </span>
                        {tx.foreign_amount && (
                          <div className="text-[10px] font-mono text-[#5C5C55] flex items-center justify-end gap-1">
                            <span className="bg-[#E8E6DE] px-1 py-0 rounded text-[9px]">{tx.country || tx.foreign_currency}</span>
                            {tx.foreign_currency} {tx.foreign_amount}
                          </div>
                        )}
                      </td>
                      {/* Edit */}
                      <td className="p-2">
                        <div className="flex items-center gap-0.5 justify-center">
                          {isEditing ? (
                            <>
                              <button onClick={() => saveEdit(tx.id)} data-testid={`biz-save-${tx.id}`} title="Save"
                                className="p-1 rounded text-[#284A33] hover:bg-[#284A33]/10 transition-all"><Save size={13} /></button>
                              <button onClick={cancelEdit} data-testid={`biz-cancel-${tx.id}`} title="Cancel"
                                className="p-1 rounded text-[#5C5C55] hover:bg-[#E8E6DE] transition-all"><X size={13} /></button>
                            </>
                          ) : (
                            <button onClick={() => startEdit(tx)} data-testid={`biz-edit-${tx.id}`} title="Edit tag & category"
                              className="p-1 rounded text-[#5C5C55] hover:text-[#284A33] hover:bg-[#284A33]/10 transition-all"><Pencil size={12} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E0D8] bg-[#F7F6F2]/30">
            <span className="text-xs text-[#5C5C55]">Page {page} of {pages}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-7 w-7 p-0"><ChevronLeft size={15} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="h-7 w-7 p-0"><ChevronRight size={15} /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Custom Sub-category Dialog */}
      <Dialog open={othersDialog.open} onOpenChange={(o) => !o && setOthersDialog({ ...othersDialog, open: false })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Custom Sub-category</DialogTitle>
            <DialogDescription>Added to your {othersDialog.parentTag} sub-categories permanently.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Name</Label>
            <Input data-testid="biz-custom-subcat-input" value={othersDialog.customName}
              onChange={(e) => setOthersDialog({ ...othersDialog, customName: e.target.value })}
              placeholder="e.g. Travel - Singapore" className="border-[#E2E0D8]"
              onKeyDown={(e) => e.key === 'Enter' && saveCustomSubCategory()} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOthersDialog({ ...othersDialog, open: false })}>Cancel</Button>
            <Button onClick={saveCustomSubCategory} className="bg-[#284A33] text-white hover:bg-[#1C3624]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Category Dialog */}
      <Dialog open={catDialog.open} onOpenChange={(o) => !o && setCatDialog({ ...catDialog, open: false })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Custom Category</DialogTitle>
            <DialogDescription>Added to your categories permanently.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Category Name</Label>
            <Input data-testid="biz-custom-category-input" value={catDialog.customName}
              onChange={(e) => setCatDialog({ ...catDialog, customName: e.target.value })}
              placeholder="e.g. Gym & Fitness" className="border-[#E2E0D8]"
              onKeyDown={(e) => e.key === 'Enter' && saveCustomCategory()} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCatDialog({ ...catDialog, open: false })}>Cancel</Button>
            <Button onClick={saveCustomCategory} className="bg-[#284A33] text-white hover:bg-[#1C3624]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link as Split Dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(o) => !o && setLinkDialog({ ...linkDialog, open: false })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Link2 size={16} className="text-blue-600" /> Link as Split Entry</DialogTitle>
            <DialogDescription>Combine {selected.size} transactions into one split entry.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Note (optional)</Label>
            <Input data-testid="biz-link-note-input" value={linkDialog.note}
              onChange={(e) => setLinkDialog({ ...linkDialog, note: e.target.value })}
              placeholder="e.g. GST split payment" className="border-[#E2E0D8]"
              onKeyDown={(e) => e.key === 'Enter' && linkSelected()} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkDialog({ ...linkDialog, open: false })}>Cancel</Button>
            <Button onClick={linkSelected} data-testid="biz-confirm-link-btn" className="bg-blue-600 text-white hover:bg-blue-700">
              <Link2 size={13} className="mr-1.5" /> Link Transactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
